import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLayoutEffect, useMemo, useRef } from 'react';
import { getTestIds } from 'seagull/utils/TestIds';
import { activeConfirmation } from '../AgentDraftingPane.utils';
import styles from './AgentChatOpenState.module.scss';
import { ChatInput } from './ChatInput';
import { DecisionSlot } from './DecisionSlot/DecisionSlot';
import { MessageThread } from './MessageThread';
const STICKY_THRESHOLD_PX = 32;
const SCROLLED_THRESHOLD_PX = 2;
export const GetAgentChatOpenStateTID = (parentTestId)=>getTestIds(parentTestId);
export const AgentChatOpenState = (param)=>{
    let { chat, isAutoSaving, placeholderPool, onSubmitPrompt, onPdfUpload, onRemoveAttachment, onDismissProposal, onApplyProposal, onListSelectClick, onNumberedListClick, onSubmitFeedback, onDismissFeedback, onPickFollowUpSuggestion, onUndoProposal, onScrolledChange, testId } = param;
    const { AgentChatOpenStateTID } = useMemo(()=>GetAgentChatOpenStateTID(testId), [
        testId
    ]);
    const bodyRef = useRef(null);
    const contentRef = useRef(null);
    const isStickyRef = useRef(true);
    const isScrolledRef = useRef(false);
    const onBodyScroll = ()=>{
        const body = bodyRef.current;
        if (body == null) return;
        const distanceFromBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
        isStickyRef.current = distanceFromBottom <= STICKY_THRESHOLD_PX;
        const scrolled = body.scrollTop > SCROLLED_THRESHOLD_PX;
        if (scrolled !== isScrolledRef.current) {
            isScrolledRef.current = scrolled;
            onScrolledChange === null || onScrolledChange === void 0 ? void 0 : onScrolledChange(scrolled);
        }
    };
    useLayoutEffect(()=>{
        const body = bodyRef.current;
        const content = contentRef.current;
        if (body == null || content == null) return;
        const observer = new ResizeObserver(()=>{
            if (!isStickyRef.current) return;
            body.scrollTop = body.scrollHeight;
        });
        observer.observe(content);
        observer.observe(body);
        return ()=>observer.disconnect();
    }, []);
    const active = activeConfirmation(chat.messages, chat.localDecisions);
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.container,
        children: [
            /*#__PURE__*/ _jsx("div", {
                ref: bodyRef,
                onScroll: onBodyScroll,
                className: styles.body,
                children: /*#__PURE__*/ _jsx("div", {
                    ref: contentRef,
                    children: /*#__PURE__*/ _jsx(MessageThread, {
                        messages: chat.messages,
                        isThinking: chat.isThinking,
                        activeToolCalls: chat.activeToolCalls,
                        thinkingText: chat.thinkingText,
                        thinkingLabel: chat.thinkingLabel,
                        error: chat.error,
                        undoPendingAppliedId: chat.undoPendingAppliedId,
                        localDecisions: chat.localDecisions,
                        onDismissProposal: onDismissProposal,
                        onApplyProposal: onApplyProposal,
                        onListSelectClick: onListSelectClick,
                        onNumberedListClick: onNumberedListClick,
                        onSubmitFeedback: onSubmitFeedback,
                        onDismissFeedback: onDismissFeedback,
                        onSuggestionClick: onPickFollowUpSuggestion,
                        onUndo: onUndoProposal,
                        testId: AgentChatOpenStateTID.MessageThread()
                    })
                })
            }),
            /*#__PURE__*/ _jsx(DecisionSlot, {
                activeMessage: active,
                onDismissProposal: onDismissProposal,
                onApplyProposal: onApplyProposal,
                onListSelectClick: onListSelectClick,
                onNumberedListClick: onNumberedListClick,
                testId: AgentChatOpenStateTID.DecisionSlot()
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.inputBlock,
                children: /*#__PURE__*/ _jsx(ChatInput, {
                    isThinking: chat.isThinking,
                    isUploading: chat.isUploading,
                    isAutoSaving: isAutoSaving,
                    uploadingFileName: chat.uploadingFileName,
                    pendingAttachment: chat.pendingAttachment,
                    placeholderPool: placeholderPool,
                    onSubmit: onSubmitPrompt,
                    onPdfUpload: onPdfUpload,
                    onRemoveAttachment: onRemoveAttachment,
                    testId: AgentChatOpenStateTID.ChatInput()
                })
            })
        ]
    });
};
